import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { createPortalApi, getBridgeOid, getBridgeEmail } from '../api/portalApi';
import { useRuntimeConfig } from '../context/RuntimeConfigContext';
import Modal from '../components/Modal';
import { Icon } from '../components/icons';
import EmptyState from '../components/EmptyState';
import NoSubmissionsIllustration from '../components/illustrations/NoSubmissionsIllustration';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import type { ReviewSession, UserAccess } from '../types';

const DEV_BYPASS = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';
const DEV_EMAIL  = 'dev@fintinc.com';

const STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  in_review: { label: 'In Review',  cls: 'badge-review'  },
  approved:  { label: 'Approved',   cls: 'badge-approved' },
  pushed:    { label: 'Pushed',     cls: 'badge-pushed'   },
  partial:   { label: 'Partial',    cls: 'badge-partial'  },
};

type SortCol = 'status' | 'createdAt' | 'updatedAt' | 'author';
type SortDir = 'asc' | 'desc';

function formatDate(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function timeAgo(iso?: string) {
  if (!iso) return '—';
  const diff  = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (days  > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (mins  > 0) return `${mins}m ago`;
  return 'just now';
}

// ── Assign Reviewers Modal ─────────────────────────────────────────────────────

interface AssignModalProps {
  session:   ReviewSession;
  orgUsers:  UserAccess[];
  onSave:    (reviewers: string[]) => Promise<void>;
  onClose:   () => void;
}

function AssignReviewersModal({ session, orgUsers, onSave, onClose }: AssignModalProps) {
  const approvedUsers = orgUsers.filter(u => u.status === 'approved');

  // Normalize legacy email-based IDs to OIDs so saved data is always OID-keyed
  function normalizeToOid(id: string): string {
    const byEmail = approvedUsers.find(u => u.email.toLowerCase() === id.toLowerCase());
    return byEmail ? byEmail.userId : id;
  }

  const normalizedInitial = (session.assignedReviewers ?? []).map(normalizeToOid);
  const originalSet             = new Set(normalizedInitial);
  const [selected, setSelected] = useState<string[]>(normalizedInitial);
  const [search, setSearch]     = useState('');
  const [saving, setSaving]     = useState(false);
  const searchRef               = useRef<HTMLInputElement>(null);

  useEffect(() => { searchRef.current?.focus(); }, []);

  const showDropdown = search.length >= 3;

  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);

  useLayoutEffect(() => {
    if (!showDropdown) { setDropdownPos(null); return; }
    function updatePos() {
      const rect = searchRef.current?.getBoundingClientRect();
      if (rect) setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [showDropdown, selected]);

  function userId(u: UserAccess) { return u.userId; }
  function findUser(id: string)  { return approvedUsers.find(u => u.userId === id); }

  const filtered = approvedUsers.filter(u => {
    if (selected.includes(userId(u))) return false;
    const q = search.toLowerCase();
    return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
  });

  function pick(id: string) {
    setSelected(prev => prev.includes(id) ? prev : [...prev, id]);
    setSearch('');
    searchRef.current?.focus();
  }

  function remove(email: string) {
    setSelected(prev => prev.filter(r => r !== email));
  }

  async function handleSave() {
    setSaving(true);
    try { await onSave(selected); }
    finally { setSaving(false); }
  }

  const existingSelected = selected.filter(e => originalSet.has(e));
  const newSelected      = selected.filter(e => !originalSet.has(e));

  return (
    <Modal
      title="Assign Reviewers"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {/* Already assigned */}
      {existingSelected.length > 0 && (
        <div className="mb-md">
          <p className="assign-section-label">Currently assigned</p>
          <div className="assign-chips">
            {existingSelected.map(id => {
              const user = findUser(id);
              return (
                <span key={id} className="assign-chip assign-chip-existing" title={user?.name || id}>
                  {user?.name || id}
                  <button className="assign-chip-remove" onClick={() => remove(id)} title="Remove"><Icon name="x" size={11} /></button>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Newly added */}
      {newSelected.length > 0 && (
        <div className="mb-md">
          <p className="assign-section-label">Adding</p>
          <div className="assign-chips">
            {newSelected.map(id => {
              const user = findUser(id);
              return (
                <span key={id} className="assign-chip" title={user?.name || id}>
                  {user?.name || id}
                  <button className="assign-chip-remove" onClick={() => remove(id)} title="Remove"><Icon name="x" size={11} /></button>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Search + dropdown */}
      <div className="assign-picker">
        <input
          ref={searchRef}
          className="form-input"
          placeholder="Type 3+ characters to search approved users…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {showDropdown && dropdownPos && (
          <div
            className="dropdown-fixed"
            style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
          >
            {filtered.length === 0 ? (
              <div className="dropdown-fixed-empty">No matching approved users</div>
            ) : (
              filtered.map(u => (
                <button
                  key={u.userId}
                  className="dropdown-fixed-item"
                  onClick={() => pick(userId(u))}
                  type="button"
                >
                  <span className="avatar avatar-sm">{(u.name || u.email || u.userId)[0]?.toUpperCase()}</span>
                  <span>
                    <span className="assign-user-name">{u.name || u.userId}</span>
                    <span className="assign-user-email">{u.email}</span>
                  </span>
                </button>
              ))
            )}
          </div>
        )}
        {search.length > 0 && search.length < 3 && (
          <div className="assign-hint-min-chars">
            Type {3 - search.length} more character{3 - search.length !== 1 ? 's' : ''} to search
          </div>
        )}
      </div>

      {selected.length === 0 && !search && (
        <p className="assign-empty">Search above to pick reviewers from your organisation.</p>
      )}
    </Modal>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function AllSessionsPage() {
  const { instance, accounts } = useMsal();
  const cfg      = useRuntimeConfig();
  const navigate = useNavigate();
  const api      = createPortalApi(instance, cfg.msalClientId, cfg.apiBaseUrl);
  const { showToast } = useToast();
  const confirmDialog  = useConfirm();

  const currentEmail = (DEV_BYPASS ? DEV_EMAIL : (accounts[0]?.username ?? getBridgeEmail())).toLowerCase();
  const currentOid   = DEV_BYPASS ? '' : (accounts[0]?.localAccountId ?? getBridgeOid());

  const [sessions, setSessions]         = useState<ReviewSession[]>([]);
  const [orgUsers, setOrgUsers]         = useState<UserAccess[]>([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [sort, setSort]                 = useState<{ col: SortCol; dir: SortDir } | null>(null);
  const [statusFilter, setStatus]       = useState('');
  const [creatorFilter, setCreator]     = useState('');
  const [reviewerFilter, setReviewer]   = useState('');
  const [assignTarget, setAssignTarget] = useState<ReviewSession | null>(null);

  // Row "Actions" dropdown (Assign reviewer / Automate / Delete)
  const [openActionsFor, setOpenActionsFor] = useState<string | null>(null);
  const [actionsMenuPos, setActionsMenuPos] = useState<{ top: number; right: number } | null>(null);
  const actionsMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    load();
    api.getOrgUsers().then(setOrgUsers).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!openActionsFor) return;
    function onClickOutside(e: MouseEvent) {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target as Node)) {
        setOpenActionsFor(null);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [openActionsFor]);

  function toggleActionsMenu(e: React.MouseEvent<HTMLButtonElement>, sessionId: string) {
    e.stopPropagation();
    if (openActionsFor === sessionId) { setOpenActionsFor(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    setActionsMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    setOpenActionsFor(sessionId);
  }

  async function load() {
    try {
      const all = await api.getSessions(false);
      setSessions(all);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  function toggleSort(col: SortCol) {
    setSort(prev => {
      if (prev?.col === col) return prev.dir === 'asc' ? { col, dir: 'desc' } : null;
      return { col, dir: 'asc' };
    });
  }

  function sortIcon(col: SortCol) {
    if (sort?.col !== col) return <span className="sort-icon sort-icon-idle">↕</span>;
    return <span className="sort-icon sort-icon-active">{sort.dir === 'asc' ? '↑' : '↓'}</span>;
  }

  async function handlePush(e: React.MouseEvent, sessionId: string) {
    e.stopPropagation();
    if (!await confirmDialog('Push all approved test cases to the configured target?', { confirmLabel: 'Push' })) return;
    try {
      const results = await api.pushSession(sessionId);
      const ok  = results.filter(r => r.success).length;
      const bad = results.filter(r => !r.success).length;
      showToast(`Push complete: ${ok} succeeded${bad ? `, ${bad} failed` : ''}.`, bad ? 'error' : 'success');
      await load();
    } catch (err: any) {
      showToast('Push failed: ' + err.message, 'error');
    }
  }

  async function handleAutomate(e: React.MouseEvent, sessionId: string) {
    e.stopPropagation();
    if (!await confirmDialog('Send all approved test cases to Web Automation?', { confirmLabel: 'Send' })) return;
    try {
      await api.automateSession(sessionId);
      showToast('Session sent to Web Automation successfully.', 'success');
      await load();
    } catch (err: any) {
      showToast('Automate failed: ' + err.message, 'error');
    }
  }

  async function handleDelete(sessionId: string) {
    if (!await confirmDialog('Delete this session and all its test cases? This cannot be undone.', { confirmLabel: 'Delete', danger: true })) return;
    try {
      await api.deleteSession(sessionId);
      setSessions(prev => prev.filter(s => s.sessionId !== sessionId));
    } catch (err: any) {
      showToast('Delete failed: ' + err.message, 'error');
    }
  }

  async function handleAssign(session: ReviewSession, reviewers: string[]) {
    await api.updateSession(session.sessionId, { assignedReviewers: reviewers });
    setSessions(prev =>
      prev.map(s => s.sessionId === session.sessionId ? { ...s, assignedReviewers: reviewers } : s),
    );
    setAssignTarget(null);
  }

  const uniqueCreators = Array.from(
    new Map(sessions.map(s => [s.authorOid, s.authorName || s.authorEmail])).entries(),
  ).sort((a, b) => a[1].localeCompare(b[1]));

  const uniqueReviewers = Array.from(
    new Set(sessions.flatMap(s => s.assignedReviewers ?? [])),
  ).map(id => {
    const user = orgUsers.find(u => u.userId === id);
    return { id, label: user?.name || user?.email || id };
  }).sort((a, b) => a.label.localeCompare(b.label));

  const filtered = sessions.filter(s => {
    if (statusFilter && s.status !== statusFilter) return false;
    if (creatorFilter && s.authorOid !== creatorFilter) return false;
    if (reviewerFilter && !(s.assignedReviewers ?? []).some(r => r === reviewerFilter)) return false;
    return true;
  });

  const hasFilter = !!(statusFilter || creatorFilter || reviewerFilter);

  const sorted = sort
    ? [...filtered].sort((a, b) => {
        if (sort.col === 'status') {
          const r = a.status.localeCompare(b.status);
          return sort.dir === 'asc' ? r : -r;
        }
        if (sort.col === 'author') {
          const r = (a.authorEmail).localeCompare(b.authorEmail);
          return sort.dir === 'asc' ? r : -r;
        }
        const key = sort.col === 'updatedAt' ? 'updatedAt' : 'createdAt';
        const ta = new Date((key === 'updatedAt' ? (a.updatedAt ?? a.createdAt) : a.createdAt)).getTime();
        const tb = new Date((key === 'updatedAt' ? (b.updatedAt ?? b.createdAt) : b.createdAt)).getTime();
        return sort.dir === 'asc' ? ta - tb : tb - ta;
      })
    : filtered;

  if (loading) return <div className="page-loading"><span className="spinner" />Loading…</div>;
  if (error)   return <div className="page-error">Failed to load: {error}</div>;

  return (
    <>
      {/* Filter bar */}
      <div className="filter-bar">
        <label className="filter-label">Status</label>
        <select className="filter-select" value={statusFilter} onChange={e => setStatus(e.target.value)}>
          <option value="">All</option>
          <option value="in_review">In Review</option>
          <option value="approved">Approved</option>
          <option value="partial">Partial</option>
          <option value="pushed">Pushed</option>
        </select>

        <label className="filter-label">Creator</label>
        <select className="filter-select" value={creatorFilter} onChange={e => setCreator(e.target.value)}>
          <option value="">All</option>
          {uniqueCreators.map(([email, name]) => (
            <option key={email} value={email}>{name}</option>
          ))}
        </select>

        <label className="filter-label">Reviewer</label>
        <select className="filter-select" value={reviewerFilter} onChange={e => setReviewer(e.target.value)}>
          <option value="">All</option>
          {uniqueReviewers.map(r => (
            <option key={r.id} value={r.id}>{r.label}</option>
          ))}
        </select>

        {hasFilter && (
          <button
            className="btn btn-sm btn-outline"
            onClick={() => { setStatus(''); setCreator(''); setReviewer(''); }}
          >
            Clear filters
          </button>
        )}
        <span className="filter-count">{sorted.length} session{sorted.length !== 1 ? 's' : ''}</span>
      </div>

      {sorted.length === 0 ? (
        <EmptyState illustration={<NoSubmissionsIllustration />}>
          {hasFilter
            ? 'No sessions match the selected filters.'
            : 'No sessions found in this organisation.'}
        </EmptyState>
      ) : (
        <div className="sessions-table-wrapper">
          <table className="sessions-table">
            <thead>
              <tr>
                <th>Session</th>
                <th
                  className="th-sortable"
                  onClick={() => toggleSort('author')}
                  title="Sort by author"
                >
                  Created by {sortIcon('author')}
                </th>
                <th>Reviewers</th>
                <th>Cases</th>
                <th>Approved</th>
                <th
                  className="th-sortable"
                  onClick={() => toggleSort('status')}
                  title="Sort by status"
                >
                  Status {sortIcon('status')}
                </th>
                <th
                  className="th-sortable"
                  onClick={() => toggleSort('createdAt')}
                  title="Sort by submitted date"
                >
                  Submitted {sortIcon('createdAt')}
                </th>
                <th
                  className="th-sortable"
                  onClick={() => toggleSort('updatedAt')}
                  title="Sort by last updated"
                >
                  Updated {sortIcon('updatedAt')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(s => {
                const badge      = STATUS_LABEL[s.status] ?? { label: s.status, cls: 'badge-review' };
                const isAuthor   = s.authorEmail.toLowerCase() === currentEmail
                                || (!!currentOid && s.authorOid === currentOid);
                const isReviewer = (s.assignedReviewers ?? [])
                                     .some(r => r.toLowerCase() === currentEmail || r === currentOid);
                const allReviewed  = s.approvedCount + s.rejectedCount === s.totalCases;
                const canPush      = isAuthor && allReviewed && s.approvedCount > 0 && s.status !== 'pushed';
                const canAutomate  = isAuthor && allReviewed && s.approvedCount > 0 && !s.automatedAt;

                return (
                  <tr
                    key={s.sessionId}
                    className="session-row"
                    onClick={() => navigate(`/sessions/${s.sessionId}`)}
                  >
                    <td className="session-title-cell">
                      <div className="session-title-cell-inner">
                        <span className="session-name">{s.title}</span>
                        <span className="session-type">{s.outputType}</span>
                        {s.comment && (
                          <span className="session-comment" title={s.comment}><Icon name="message" size={11} /> {s.comment}</span>
                        )}
                      </div>
                    </td>

                    <td className="session-author">
                      <div className="session-author-inner">
                        <span className="avatar" title={s.authorEmail}>
                          {(s.authorName || s.authorEmail)[0].toUpperCase()}
                        </span>
                        <span>
                          <span className="author-name">{s.authorName || s.authorEmail}</span>
                          <span className="author-email">{s.authorEmail}</span>
                        </span>
                      </div>
                    </td>

                    <td>
                      {(() => {
                        const reviewers = (s.assignedReviewers ?? []).filter(Boolean);
                        return reviewers.length > 0 ? (
                          <div className="reviewer-list">
                            {reviewers.map(id => {
                              const u = orgUsers.find(x => x.email === id || x.userId === id);
                              const display = u?.name || id;
                              return (
                                <span key={id} className="avatar" title={display}>
                                  {display[0]?.toUpperCase() ?? '?'}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="no-reviewers">—</span>
                        );
                      })()}
                    </td>

                    <td className="tc-count">{s.totalCases}</td>
                    <td className="tc-count">
                      <span className={s.approvedCount === s.totalCases ? 'all-approved' : ''}>
                        {s.approvedCount}/{s.totalCases}
                      </span>
                    </td>

                    <td>
                      <span className={`badge ${badge.cls}`}>{badge.label}</span>
                    </td>

                    <td className="session-date" title={formatDate(s.createdAt)}>
                      {timeAgo(s.createdAt)}
                    </td>
                    <td className="session-date" title={formatDate(s.updatedAt)}>
                      {s.updatedAt ? timeAgo(s.updatedAt) : '—'}
                    </td>

                    <td className="session-actions" onClick={e => e.stopPropagation()}>
                      <div className="session-actions-inner">
                        {isAuthor ? (
                          <>
                            {canPush && (
                              <button
                                className="btn btn-sm btn-primary"
                                title="Push approved cases"
                                onClick={e => handlePush(e, s.sessionId)}
                              >
                                Push ↑
                              </button>
                            )}
                            <div
                              className="row-actions-menu-wrapper"
                              ref={openActionsFor === s.sessionId ? actionsMenuRef : undefined}
                            >
                              <button
                                className="btn btn-sm btn-outline row-actions-trigger"
                                title="More actions"
                                onClick={e => toggleActionsMenu(e, s.sessionId)}
                              >
                                <Icon name="chevron-down" size={14} />
                              </button>
                              {openActionsFor === s.sessionId && actionsMenuPos && (
                                <div className="row-actions-menu" style={{ top: actionsMenuPos.top, right: actionsMenuPos.right }}>
                                  {s.status !== 'pushed' && (
                                    <button
                                      className="row-actions-item"
                                      onClick={() => { setOpenActionsFor(null); setAssignTarget(s); }}
                                    >
                                      <Icon name="users" size={14} /> Assign reviewer
                                    </button>
                                  )}
                                  {canAutomate && (
                                    <button
                                      className="row-actions-item"
                                      onClick={e => { setOpenActionsFor(null); handleAutomate(e, s.sessionId); }}
                                    >
                                      <Icon name="zap" size={14} /> Automate
                                    </button>
                                  )}
                                  {(s.status !== 'pushed' || canAutomate) && <div className="row-actions-divider" />}
                                  <button
                                    className="row-actions-item row-actions-item-danger"
                                    onClick={() => { setOpenActionsFor(null); handleDelete(s.sessionId); }}
                                  >
                                    <Icon name="trash" size={14} /> Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          </>
                        ) : isReviewer ? (
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => navigate(`/sessions/${s.sessionId}`)}
                          >
                            Review →
                          </button>
                        ) : (
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => navigate(`/sessions/${s.sessionId}`)}
                          >
                            View →
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {assignTarget && (
        <AssignReviewersModal
          session={assignTarget}
          orgUsers={orgUsers}
          onSave={reviewers => handleAssign(assignTarget, reviewers)}
          onClose={() => setAssignTarget(null)}
        />
      )}
    </>
  );
}
